"use client";

import { useCallback, useEffect, useRef } from "react";
import type { AutoTrackingOptions, AutoTrackingResolver } from "../auto-tracking";

export type { AutoTrackingOptions } from "../auto-tracking";

export type UseAutoTrackingOptions = AutoTrackingOptions & {
	getAnalytics: AutoTrackingResolver;
};

const TEXT_INPUT_TYPES = ["text", "email", "password", "search", "tel", "url", "number", "date"] as const;
const CUSTOM_SELECTORS = ["[data-track-click]"];

/**
 * Finds the associated label for a form element.
 * Checks both the label[for] attribute and parent label element.
 */
function findAssociatedLabel(element: HTMLElement): string {
	if (element.id) {
		const label = document.querySelector(`label[for="${element.id}"]`);
		if (label?.textContent) {
			return label.textContent.trim();
		}
	}

	const parentLabel = element.closest("label");
	if (parentLabel?.textContent) {
		return parentLabel.textContent.trim();
	}

	return "";
}

/**
 * Extracts descriptive text from an HTML element for analytics tracking.
 * Priority: associated label > text content > placeholder > aria-label > name attribute
 */
function getElementText(element: HTMLElement): string {
	if (element.hasAttribute("data-track-click")) {
		const textParts: string[] = [];

		const collectText = (node: Element) => {
			// Get direct text nodes (excluding nested element text)
			for (const child of Array.from(node.childNodes)) {
				if (child.nodeType === Node.TEXT_NODE) {
					const text = child.textContent?.trim();
					if (text) {
						textParts.push(text);
					}
				} else if (child.nodeType === Node.ELEMENT_NODE) {
					collectText(child as Element);
				}
			}
		};

		collectText(element);

		if (textParts.length > 0) {
			return textParts.join(" - ");
		}
	}

	if (element instanceof HTMLInputElement) {
		const labelText = findAssociatedLabel(element);
		if (labelText) return labelText;

		if (TEXT_INPUT_TYPES.includes(element.type as (typeof TEXT_INPUT_TYPES)[number])) {
			return element.placeholder || element.name || "";
		}

		return element.value || element.name || "";
	}

	if (element instanceof HTMLTextAreaElement) {
		const labelText = findAssociatedLabel(element);
		if (labelText) return labelText;

		return element.placeholder || element.name || "";
	}

	return element.textContent?.trim() || element.getAttribute("aria-label") || "";
}

function getElementType(element: HTMLElement): string {
	// Native HTML elements (checked first for performance)
	if (element instanceof HTMLButtonElement) {
		return "button";
	}

	if (element instanceof HTMLAnchorElement) {
		return "link";
	}

	if (element instanceof HTMLTextAreaElement) {
		return "textarea";
	}

	if (element instanceof HTMLInputElement) {
		switch (element.type) {
			case "button":
				return "button";
			case "submit":
				return "submit-button";
			case "radio":
				return "radio";
			case "checkbox":
				return "checkbox";
			default:
				if (TEXT_INPUT_TYPES.includes(element.type as (typeof TEXT_INPUT_TYPES)[number])) {
					return "input";
				}
		}
	}

	const role = element.getAttribute("role");
	if (role) {
		switch (role) {
			case "button":
				return "button";
			case "radio":
				return "radio";
			case "checkbox":
				return "checkbox";
		}
	}

	return "element";
}

function buildClickSelector(options: AutoTrackingOptions): string {
	const selectors: string[] = [];

	if (options.trackButtons !== false) {
		selectors.push("button", '[role="button"]', 'input[type="submit"]', 'input[type="button"]');
	}

	if (options.trackRadioButtons !== false) {
		selectors.push('input[type="radio"]', '[role="radio"]');
	}

	if (options.trackCheckboxes !== false) {
		selectors.push('input[type="checkbox"]', '[role="checkbox"]');
	}

	if (options.trackLinks !== false) {
		selectors.push("a[href]");
	}

	selectors.push(...CUSTOM_SELECTORS, ...(options.customSelectors || []));

	return selectors.join(", ");
}

function buildInputSelector(options: AutoTrackingOptions): string {
	if (options.trackInputs === false) {
		return "";
	}

	const inputSelectors = TEXT_INPUT_TYPES.map((type) => `input[type="${type}"]`);
	return [...inputSelectors, "textarea"].join(", ");
}

/**
 * Custom hook that automatically tracks user interactions with DOM elements.
 * Monitors clicks, input focus, and input changes based on provided options.
 *
 * @param options - Configuration for tracking behavior and analytics handler
 */
export function useAutoTracking(options: UseAutoTrackingOptions): void {
	const { getAnalytics } = options;
	const trackedInputsRef = useRef<WeakSet<HTMLElement>>(new WeakSet());
	const _pathname = typeof window !== "undefined" ? window.location.pathname : "";

	useEffect(() => {
		trackedInputsRef.current = new WeakSet();
	}, []);

	/**
	 * Handles click events on tracked elements (buttons, links, checkboxes, radios).
	 */
	const handleClick = useCallback(
		(event: MouseEvent) => {
			const target = event.target as HTMLElement;
			if (!target) return;

			const selector = buildClickSelector(options);
			if (!selector) return;

			const trackedElement = target.closest<HTMLElement>(selector);
			if (!trackedElement) return;

			try {
				const analytics = getAnalytics();
				const elementText = getElementText(trackedElement);
				const elementType = getElementType(trackedElement);
				const pagePath = typeof window !== "undefined" ? window.location.pathname : undefined;

				analytics.trackClick({
					label: elementText || undefined,
					elementType,
					metadata: {
						page_path: pagePath,
					},
				});
			} catch (error) {
				console.debug("[Analytics] Auto-click tracking skipped:", error);
			}
		},
		[options, getAnalytics],
	);

	/**
	 * Handles focus events on text input elements.
	 */
	const handleInputFocus = useCallback(
		(event: FocusEvent) => {
			const target = event.target as HTMLElement;
			if (!target) return;

			const selector = buildInputSelector(options);
			if (!selector) return;

			const trackedElement = target.closest<HTMLElement>(selector);
			if (!trackedElement) return;

			try {
				const analytics = getAnalytics();
				const elementText = getElementText(trackedElement);
				const elementType = getElementType(trackedElement);
				const pagePath = typeof window !== "undefined" ? window.location.pathname : undefined;

				analytics.trackFocus({
					label: elementText || elementType,
					elementType,
					metadata: {
						page_path: pagePath,
					},
				});
			} catch (error) {
				console.debug("[Analytics] Auto-input focus tracking skipped:", error);
			}
		},
		[options, getAnalytics],
	);

	/**
	 * Handles input change events. Only tracks the first change per element per page.
	 */
	const handleInputChange = useCallback(
		(event: Event) => {
			const target = event.target as HTMLElement;
			if (!target) return;

			const selector = buildInputSelector(options);
			if (!selector) return;

			const trackedElement = target.closest<HTMLElement>(selector);
			if (!trackedElement) return;

			// Only track the first input event for each element to avoid noise
			if (trackedInputsRef.current.has(trackedElement)) return;
			trackedInputsRef.current.add(trackedElement);

			try {
				const analytics = getAnalytics();
				const elementText = getElementText(trackedElement);
				const elementType = getElementType(trackedElement);
				const pagePath = typeof window !== "undefined" ? window.location.pathname : undefined;

				analytics.trackInput({
					label: elementText || elementType,
					elementType,
					metadata: {
						page_path: pagePath,
					},
				});
			} catch (error) {
				console.debug("[Analytics] Auto-input change tracking skipped:", error);
			}
		},
		[options, getAnalytics],
	);

	useEffect(() => {
		const eventOptions = { capture: true };

		document.addEventListener("click", handleClick, eventOptions);

		if (options.trackInputs !== false) {
			document.addEventListener("focusin", handleInputFocus, eventOptions);
			document.addEventListener("input", handleInputChange, eventOptions);
		}

		return () => {
			document.removeEventListener("click", handleClick, eventOptions);
			document.removeEventListener("focusin", handleInputFocus, eventOptions);
			document.removeEventListener("input", handleInputChange, eventOptions);
		};
	}, [handleClick, handleInputFocus, handleInputChange, options.trackInputs]);
}
